import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, UserCheck, AlertCircle, CheckCircle, XCircle, Plus, Trash2, Move, History, Filter, Mail } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

// Demo data
const PERIODS = [
  { id: 1, name: 'Period 1', start_time: '08:00', end_time: '08:50' },
  { id: 2, name: 'Period 2', start_time: '08:55', end_time: '09:45' },
  { id: 3, name: 'Period 3', start_time: '09:50', end_time: '10:40' },
  { id: 4, name: 'Break', start_time: '10:40', end_time: '11:00' },
  { id: 5, name: 'Period 4', start_time: '11:00', end_time: '11:50' },
  { id: 6, name: 'Period 5', start_time: '11:55', end_time: '12:45' },
  { id: 7, name: 'Lunch', start_time: '12:45', end_time: '13:30' },
  { id: 8, name: 'Period 6', start_time: '13:30', end_time: '14:20' },
  { id: 9, name: 'Period 7', start_time: '14:25', end_time: '15:15' },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const TEACHERS = [
  { staffid: 'ST048', fullname: 'Dr. Sarah Johnson', canEdit: true },
  { staffid: 'ST052', fullname: 'Mr. David Chen', canEdit: false },
  { staffid: 'ST061', fullname: 'Ms. Emily Parker', canEdit: false },
  { staffid: 'ST073', fullname: 'Prof. Michael Brown', canEdit: false },
  { staffid: 'ST089', fullname: 'Mrs. Lisa Martinez', canEdit: false },
];

const COURSES = [
  { course_number: '0101', course_name: 'English Literature 11', grade_level: 'G11', color: 'e74c3c' },
  { course_number: '0210', course_name: 'IB Mathematics HL', grade_level: 'G12', color: '3498db' },
  { course_number: '0215', course_name: 'IB Mathematics SL', grade_level: 'G11', color: '3498db' },
  { course_number: '0310', course_name: 'Physics 12', grade_level: 'G12', color: '9b59b6' },
  { course_number: '0410', course_name: 'World History 10', grade_level: 'G10', color: 'e67e22' },
  { course_number: '0501', course_name: 'Spanish Advanced', grade_level: 'G11', color: 'f39c12' },
  { course_number: '0601', course_name: 'Visual Arts', grade_level: 'G10', color: '1abc9c' },
  { course_number: '0702', course_name: 'Biology Lab', grade_level: 'G11', color: '27ae60' },
];

const INITIAL_SCHEDULE = [
  { id: 1, day: 0, period: 1, course: '0101', teacher: 'ST048', room: 'A201' },
  { id: 2, day: 0, period: 2, course: '0210', teacher: 'ST052', room: 'B105' },
  { id: 3, day: 0, period: 5, course: '0310', teacher: 'ST073', room: 'C302' },
  { id: 4, day: 1, period: 1, course: '0101', teacher: 'ST048', room: 'A201' },
  { id: 5, day: 1, period: 3, course: '0410', teacher: 'ST061', room: 'A305' },
  { id: 6, day: 1, period: 8, course: '0501', teacher: 'ST089', room: 'B202' },
  { id: 7, day: 2, period: 2, course: '0210', teacher: 'ST052', room: 'B105' },
  { id: 8, day: 2, period: 5, course: '0601', teacher: 'ST061', room: 'Art Studio' },
  { id: 9, day: 3, period: 1, course: '0702', teacher: 'ST073', room: 'Lab 1' },
  { id: 10, day: 3, period: 3, course: '0215', teacher: 'ST052', room: 'B105' },
  { id: 11, day: 4, period: 2, course: '0310', teacher: 'ST073', room: 'C302' },
  { id: 12, day: 4, period: 5, course: '0410', teacher: 'ST061', room: 'A305' },
];

const INITIAL_COVER_AVAILABILITY = [
  { staffid: 'ST048', day: 1, period: 2, available: true },
  { staffid: 'ST048', day: 2, period: 5, available: true },
  { staffid: 'ST052', day: 0, period: 5, available: true },
  { staffid: 'ST061', day: 3, period: 2, available: true },
  { staffid: 'ST073', day: 4, period: 8, available: true },
];

const IshworldTimetableCoverDemo = () => {
  const [view, setView] = useState('timetable'); // 'timetable', 'cover', 'requests', 'history'
  const [schedule, setSchedule] = useState(INITIAL_SCHEDULE);
  const [coverAvailability, setCoverAvailability] = useState(INITIAL_COVER_AVAILABILITY);
  const [coverRequests, setCoverRequests] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [userRole, setUserRole] = useState('sadmin'); // 'sadmin' or 'teacher'
  const [draggedLesson, setDraggedLesson] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [addModalData, setAddModalData] = useState({ day: 0, period: 1 });

  // Generate color from course name
  const getCourseColor = (courseName) => {
    const course = COURSES.find(c => c.course_number === courseName);
    return course?.color || '95a5a6';
  };

  // Get avatar/initials
  const getTeacherAvatar = (staffid) => {
    const teacher = TEACHERS.find(t => t.staffid === staffid);
    if (!teacher) return null;
    
    const names = teacher.fullname.split(' ');
    const initials = names.length >= 2 
      ? names[0][0] + names[names.length - 1][0]
      : names[0].substring(0, 2);
    
    const hash = staffid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colors = ['#3498db', '#e74c3c', '#9b59b6', '#f39c12', '#1abc9c'];
    const color = colors[hash % colors.length];
    
    return (
      <div className="d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
           style={{ width: 32, height: 32, backgroundColor: color, fontSize: 12 }}>
        {initials}
      </div>
    );
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Add lesson
  const addLesson = (day, period, courseNum, room) => {
    if (userRole !== 'sadmin') {
      // Create change request
      const request = {
        id: Date.now(),
        type: 'create',
        day,
        period,
        course: courseNum,
        room,
        teacher: selectedTeacher,
        status: 'pending',
        requestedBy: 'Current User',
        requestedAt: new Date()
      };
      setChangeRequests([...changeRequests, request]);
      showNotification('Change request submitted for admin approval', 'info');
      return;
    }

    const newLesson = {
      id: Date.now(),
      day,
      period,
      course: courseNum,
      teacher: selectedTeacher,
      room
    };
    setSchedule([...schedule, newLesson]);
    
    const historyEntry = {
      id: Date.now(),
      action: 'create_lesson',
      user: 'Admin User',
      timestamp: new Date(),
      details: `Added ${COURSES.find(c => c.course_number === courseNum)?.course_name} on ${DAYS[day]} Period ${period}`
    };
    setHistory([historyEntry, ...history]);
    showNotification('Lesson added successfully');
  };

  // Delete lesson
  const deleteLesson = (lessonId) => {
    const lesson = schedule.find(l => l.id === lessonId);
    if (!lesson) return;

    if (userRole !== 'sadmin') {
      const request = {
        id: Date.now(),
        type: 'delete',
        lessonId,
        lesson,
        status: 'pending',
        requestedBy: 'Current User',
        requestedAt: new Date()
      };
      setChangeRequests([...changeRequests, request]);
      showNotification('Delete request submitted for admin approval', 'info');
      return;
    }

    setSchedule(schedule.filter(l => l.id !== lessonId));
    const course = COURSES.find(c => c.course_number === lesson.course);
    const historyEntry = {
      id: Date.now(),
      action: 'delete_lesson',
      user: 'Admin User',
      timestamp: new Date(),
      details: `Deleted ${course?.course_name} from ${DAYS[lesson.day]} Period ${lesson.period}`
    };
    setHistory([historyEntry, ...history]);
    showNotification('Lesson deleted successfully');
  };

  // Move lesson (drag and drop)
  const moveLesson = (lessonId, newDay, newPeriod) => {
    const lesson = schedule.find(l => l.id === lessonId);
    if (!lesson) return;

    if (userRole !== 'sadmin') {
      const request = {
        id: Date.now(),
        type: 'move',
        lessonId,
        oldDay: lesson.day,
        oldPeriod: lesson.period,
        newDay,
        newPeriod,
        lesson,
        status: 'pending',
        requestedBy: 'Current User',
        requestedAt: new Date()
      };
      setChangeRequests([...changeRequests, request]);
      showNotification('Move request submitted for admin approval', 'info');
      return;
    }

    const updatedSchedule = schedule.map(l => 
      l.id === lessonId ? { ...l, day: newDay, period: newPeriod } : l
    );
    setSchedule(updatedSchedule);
    
    const course = COURSES.find(c => c.course_number === lesson.course);
    const historyEntry = {
      id: Date.now(),
      action: 'move_lesson',
      user: 'Admin User',
      timestamp: new Date(),
      details: `Moved ${course?.course_name} from ${DAYS[lesson.day]} Period ${lesson.period} to ${DAYS[newDay]} Period ${newPeriod}`
    };
    setHistory([historyEntry, ...history]);
    showNotification('Lesson moved successfully');
  };

  // Process change request
  const processChangeRequest = (requestId, approved) => {
    const request = changeRequests.find(r => r.id === requestId);
    if (!request) return;

    if (approved) {
      if (request.type === 'create') {
        const newLesson = {
          id: Date.now(),
          day: request.day,
          period: request.period,
          course: request.course,
          teacher: request.teacher,
          room: request.room
        };
        setSchedule([...schedule, newLesson]);
      } else if (request.type === 'delete') {
        setSchedule(schedule.filter(l => l.id !== request.lessonId));
      } else if (request.type === 'move') {
        const updatedSchedule = schedule.map(l => 
          l.id === request.lessonId 
            ? { ...l, day: request.newDay, period: request.newPeriod } 
            : l
        );
        setSchedule(updatedSchedule);
      }

      const historyEntry = {
        id: Date.now(),
        action: `request_approved_${request.type}`,
        user: 'Admin User',
        timestamp: new Date(),
        details: `Approved ${request.type} request from ${request.requestedBy}`
      };
      setHistory([historyEntry, ...history]);
      showNotification('Request approved and applied', 'success');
    } else {
      showNotification('Request rejected', 'warning');
    }

    setChangeRequests(changeRequests.filter(r => r.id !== requestId));
  };

  // Toggle cover availability
  const toggleCoverAvailability = (staffid, day, period) => {
    const existing = coverAvailability.find(
      ca => ca.staffid === staffid && ca.day === day && ca.period === period
    );

    if (existing) {
      setCoverAvailability(
        coverAvailability.filter(
          ca => !(ca.staffid === staffid && ca.day === day && ca.period === period)
        )
      );
      showNotification('Availability removed');
    } else {
      setCoverAvailability([
        ...coverAvailability,
        { staffid, day, period, available: true }
      ]);
      showNotification('Marked as available for cover');
    }
  };

  // Request cover
  const requestCover = (day, period, reason) => {
    const request = {
      id: Date.now(),
      day,
      period,
      reason,
      status: 'pending',
      requestedBy: selectedTeacher || 'ST048',
      requestedAt: new Date()
    };
    setCoverRequests([...coverRequests, request]);
    showNotification('Cover request submitted', 'info');
  };

  // Assign cover
  const assignCover = (requestId, coverTeacher) => {
    const updatedRequests = coverRequests.map(r =>
      r.id === requestId
        ? { ...r, status: 'assigned', coverTeacher, assignedAt: new Date() }
        : r
    );
    setCoverRequests(updatedRequests);
    
    const historyEntry = {
      id: Date.now(),
      action: 'cover_assigned',
      user: 'Admin User',
      timestamp: new Date(),
      details: `Assigned ${TEACHERS.find(t => t.staffid === coverTeacher)?.fullname} for cover`
    };
    setHistory([historyEntry, ...history]);
    showNotification('Cover assigned and teacher notified', 'success');
  };

  // Handle drag and drop
  const handleDragStart = (e, lesson) => {
    setDraggedLesson(lesson);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, day, period) => {
    e.preventDefault();
    if (draggedLesson && period !== 4 && period !== 7) { // Not break/lunch
      moveLesson(draggedLesson.id, day, period);
      setDraggedLesson(null);
    }
  };

  // Render timetable grid
  const renderTimetableGrid = () => {
    const filteredSchedule = selectedTeacher
      ? schedule.filter(l => l.teacher === selectedTeacher)
      : schedule;

    return (
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th style={{ width: 120 }}>Time</th>
              {DAYS.map((day, idx) => (
                <th key={idx} className="text-center">{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERIODS.map(period => (
              <tr key={period.id}>
                <td className="align-middle">
                  <div className="fw-semibold">{period.name}</div>
                  <small className="text-muted">
                    {period.start_time} - {period.end_time}
                  </small>
                </td>
                {DAYS.map((day, dayIdx) => {
                  const lesson = filteredSchedule.find(
                    l => l.day === dayIdx && l.period === period.id
                  );
                  const isBreak = period.name === 'Break' || period.name === 'Lunch';

                  return (
                    <td
                      key={dayIdx}
                      className={`align-top ${isBreak ? 'table-secondary' : ''}`}
                      style={{ minHeight: 80, position: 'relative' }}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, dayIdx, period.id)}
                    >
                      {isBreak ? (
                        <div className="text-center text-muted py-3">
                          <Clock size={20} />
                        </div>
                      ) : lesson ? (
                        <div
                          className="lesson-card p-2 rounded shadow-sm"
                          draggable={userRole === 'sadmin'}
                          onDragStart={(e) => handleDragStart(e, lesson)}
                          style={{
                            borderLeft: `4px solid #${getCourseColor(lesson.course)}`,
                            cursor: userRole === 'sadmin' ? 'move' : 'default',
                            backgroundColor: 'white'
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <small className="fw-bold">
                              {COURSES.find(c => c.course_number === lesson.course)?.course_name}
                            </small>
                            <button
                              className="btn btn-sm btn-link text-danger p-0"
                              onClick={() => deleteLesson(lesson.id)}
                              title={userRole === 'sadmin' ? 'Delete' : 'Request Delete'}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {getTeacherAvatar(lesson.teacher)}
                            <div className="flex-grow-1" style={{ fontSize: 11 }}>
                              <div className="text-muted">
                                {TEACHERS.find(t => t.staffid === lesson.teacher)?.fullname.split(' ').pop()}
                              </div>
                              <div className="text-muted">{lesson.room}</div>
                            </div>
                          </div>
                        </div>
                      ) : selectedTeacher ? (
                        <button
                          className="btn btn-sm btn-outline-primary w-100"
                          onClick={() => {
                            setAddModalData({ day: dayIdx, period: period.id });
                            setShowAddModal(true);
                          }}
                        >
                          <Plus size={16} />
                        </button>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render cover availability grid
  const renderCoverAvailabilityGrid = () => {
    const teacherStaffId = selectedTeacher || 'ST048';

    return (
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th style={{ width: 120 }}>Period</th>
              {DAYS.map((day, idx) => (
                <th key={idx} className="text-center">{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERIODS.filter(p => p.name !== 'Break' && p.name !== 'Lunch').map(period => (
              <tr key={period.id}>
                <td className="align-middle">
                  <div className="fw-semibold">{period.name}</div>
                  <small className="text-muted">{period.start_time}</small>
                </td>
                {DAYS.map((day, dayIdx) => {
                  const hasLesson = schedule.find(
                    l => l.teacher === teacherStaffId && l.day === dayIdx && l.period === period.id
                  );
                  const isAvailable = coverAvailability.find(
                    ca => ca.staffid === teacherStaffId && ca.day === dayIdx && ca.period === period.id
                  );

                  return (
                    <td key={dayIdx} className="text-center align-middle">
                      {hasLesson ? (
                        <span className="badge bg-secondary">Teaching</span>
                      ) : (
                        <button
                          className={`btn btn-sm ${isAvailable ? 'btn-success' : 'btn-outline-secondary'}`}
                          onClick={() => toggleCoverAvailability(teacherStaffId, dayIdx, period.id)}
                        >
                          {isAvailable ? <CheckCircle size={16} /> : <XCircle size={16} />}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render cover requests dashboard
  const renderCoverRequestsDashboard = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Cover Requests</h4>
        <button className="btn btn-primary" onClick={() => requestCover(0, 1, 'Medical appointment')}>
          <Plus size={18} className="me-2" />
          Request Cover
        </button>
      </div>

      {coverRequests.length === 0 ? (
        <div className="alert alert-info">No cover requests at this time</div>
      ) : (
        <div className="row g-3">
          {coverRequests.map(request => {
            const availableTeachers = coverAvailability.filter(
              ca => ca.day === request.day && ca.period === request.period
            );

            return (
              <div key={request.id} className="col-md-6">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0">
                        {DAYS[request.day]} - Period {request.period}
                      </h6>
                      <span className={`badge bg-${request.status === 'pending' ? 'warning' : 'success'}`}>
                        {request.status.toUpperCase()}
                      </span>
                    </div>

                    <p className="text-muted mb-2">
                      <strong>Reason:</strong> {request.reason}
                    </p>
                    <p className="text-muted small mb-3">
                      Requested by: {TEACHERS.find(t => t.staffid === request.requestedBy)?.fullname}
                    </p>

                    {request.status === 'pending' && userRole === 'sadmin' && (
                      <div>
                        <strong className="d-block mb-2">Available Teachers:</strong>
                        {availableTeachers.length === 0 ? (
                          <div className="alert alert-warning small py-2">
                            No teachers marked as available
                          </div>
                        ) : (
                          <div className="d-flex gap-2 flex-wrap">
                            {availableTeachers.map(at => (
                              <button
                                key={at.staffid}
                                className="btn btn-sm btn-outline-success"
                                onClick={() => assignCover(request.id, at.staffid)}
                              >
                                <UserCheck size={14} className="me-1" />
                                {TEACHERS.find(t => t.staffid === at.staffid)?.fullname.split(' ').pop()}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {request.status === 'assigned' && (
                      <div className="alert alert-success small py-2 mb-0">
                        <Mail size={14} className="me-1" />
                        Assigned to {TEACHERS.find(t => t.staffid === request.coverTeacher)?.fullname}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render change requests
  const renderChangeRequests = () => (
    <div>
      <h4 className="mb-3">Pending Change Requests</h4>
      {changeRequests.length === 0 ? (
        <div className="alert alert-info">No pending change requests</div>
      ) : (
        <div className="list-group">
          {changeRequests.map(request => (
            <div key={request.id} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="mb-1">
                    {request.type === 'create' && 'Add Lesson Request'}
                    {request.type === 'delete' && 'Delete Lesson Request'}
                    {request.type === 'move' && 'Move Lesson Request'}
                  </h6>
                  <p className="mb-1 text-muted">
                    {request.type === 'create' && `${COURSES.find(c => c.course_number === request.course)?.course_name} on ${DAYS[request.day]} Period ${request.period}`}
                    {request.type === 'delete' && `${COURSES.find(c => c.course_number === request.lesson?.course)?.course_name} from ${DAYS[request.lesson?.day]} Period ${request.lesson?.period}`}
                    {request.type === 'move' && `${COURSES.find(c => c.course_number === request.lesson?.course)?.course_name} from ${DAYS[request.oldDay]} Period ${request.oldPeriod} to ${DAYS[request.newDay]} Period ${request.newPeriod}`}
                  </p>
                  <small className="text-muted">
                    Requested by {request.requestedBy} on {request.requestedAt.toLocaleString()}
                  </small>
                </div>
                {userRole === 'sadmin' && (
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => processChangeRequest(request.id, true)}
                    >
                      <CheckCircle size={16} className="me-1" />
                      Approve
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => processChangeRequest(request.id, false)}
                    >
                      <XCircle size={16} className="me-1" />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render history
  const renderHistory = () => (
    <div>
      <h4 className="mb-3">Change History</h4>
      {history.length === 0 ? (
        <div className="alert alert-info">No history records yet</div>
      ) : (
        <div className="list-group">
          {history.map(entry => (
            <div key={entry.id} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className={`badge bg-${
                      entry.action.includes('create') ? 'success' :
                      entry.action.includes('delete') ? 'danger' :
                      entry.action.includes('move') ? 'warning' :
                      'info'
                    }`}>
                      {entry.action.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <small className="text-muted">by {entry.user}</small>
                  </div>
                  <p className="mb-0">{entry.details}</p>
                  <small className="text-muted">{entry.timestamp.toLocaleString()}</small>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1400 }}>
      {/* Header */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h2 className="mb-2">
                <Calendar className="me-2" size={28} />
                ISHWorld Timetable & Cover Demo
              </h2>
              <p className="text-muted mb-0">Interactive demonstration of schedule and cover management</p>
            </div>
            <div className="col-md-6">
              <div className="d-flex gap-2 justify-content-md-end flex-wrap">
                <select
                  className="form-select"
                  style={{ width: 200 }}
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                >
                  <option value="sadmin">Admin (Full Access)</option>
                  <option value="teacher">Teacher (Request Only)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`alert alert-${notification.type} alert-dismissible fade show`}>
          {notification.message}
          <button className="btn-close" onClick={() => setNotification(null)}></button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="btn-group w-100" role="group">
            <button
              className={`btn ${view === 'timetable' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('timetable')}
            >
              <Calendar size={18} className="me-1" />
              Master Timetable
            </button>
            <button
              className={`btn ${view === 'cover' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('cover')}
            >
              <UserCheck size={18} className="me-1" />
              Cover Availability
            </button>
            <button
              className={`btn ${view === 'cover-requests' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('cover-requests')}
            >
              <AlertCircle size={18} className="me-1" />
              Cover Requests
              {coverRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="badge bg-danger ms-1">
                  {coverRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              className={`btn ${view === 'requests' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('requests')}
            >
              <AlertCircle size={18} className="me-1" />
              Change Requests
              {changeRequests.length > 0 && (
                <span className="badge bg-warning ms-1">{changeRequests.length}</span>
              )}
            </button>
            <button
              className={`btn ${view === 'history' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView('history')}
            >
              <History size={18} className="me-1" />
              History
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {(view === 'timetable' || view === 'cover') && (
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-3">
                <label className="form-label">
                  <Filter size={16} className="me-1" />
                  Filter by Teacher
                </label>
                <select
                  className="form-select"
                  value={selectedTeacher || ''}
                  onChange={(e) => setSelectedTeacher(e.target.value || null)}
                >
                  <option value="">All Teachers</option>
                  {TEACHERS.map(teacher => (
                    <option key={teacher.staffid} value={teacher.staffid}>
                      {teacher.fullname}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-9 d-flex align-items-end justify-content-end">
                {userRole === 'sadmin' && view === 'timetable' && (
                  <div className="alert alert-info mb-0 py-2 px-3">
                    <Move size={16} className="me-2" />
                    <small>Drag lessons to move them between periods</small>
                  </div>
                )}
                {userRole === 'teacher' && view === 'timetable' && (
                  <div className="alert alert-warning mb-0 py-2 px-3">
                    <AlertCircle size={16} className="me-2" />
                    <small>Changes require admin approval</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="card shadow">
        <div className="card-body">
          {view === 'timetable' && renderTimetableGrid()}
          {view === 'cover' && renderCoverAvailabilityGrid()}
          {view === 'cover-requests' && renderCoverRequestsDashboard()}
          {view === 'requests' && renderChangeRequests()}
          {view === 'history' && renderHistory()}
        </div>
      </div>

      {/* Add Lesson Modal */}
      {showAddModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Lesson</h5>
                <button className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Course</label>
                  <select className="form-select" id="courseSelect">
                    {COURSES.map(course => (
                      <option key={course.course_number} value={course.course_number}>
                        {course.course_name} ({course.grade_level})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Room</label>
                  <input type="text" className="form-control" id="roomInput" placeholder="e.g., A201" />
                </div>
                <p className="text-muted small">
                  Adding to: {DAYS[addModalData.day]}, Period {addModalData.period}
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const courseNum = document.getElementById('courseSelect').value;
                    const room = document.getElementById('roomInput').value || 'TBA';
                    addLesson(addModalData.day, addModalData.period, courseNum, room);
                    setShowAddModal(false);
                  }}
                >
                  {userRole === 'sadmin' ? 'Add Lesson' : 'Request Addition'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Highlights */}
      <div className="card shadow-sm mt-4">
        <div className="card-header bg-light">
          <h5 className="mb-0">✨ Interactive Features Demo</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <h6><Calendar size={18} className="me-2 text-primary" />Master Timetable</h6>
              <ul className="small mb-0">
                <li>Weekly schedule grid (Monday-Friday)</li>
                <li>Color-coded lesson cards by course</li>
                <li>Teacher filter to view individual schedules</li>
                <li>Drag-and-drop lesson rearrangement (admin)</li>
                <li>Quick add/delete buttons</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6><UserCheck size={18} className="me-2 text-success" />Cover System</h6>
              <ul className="small mb-0">
                <li>Mark periods as available for cover duty</li>
                <li>Request cover when absent</li>
                <li>System suggests available teachers</li>
                <li>Admin assigns cover with notifications</li>
                <li>Real-time availability tracking</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6><AlertCircle size={18} className="me-2 text-warning" />Permission System</h6>
              <ul className="small mb-0">
                <li><strong>Admin:</strong> Direct create/edit/delete access</li>
                <li><strong>Teacher:</strong> Submit change requests</li>
                <li>Request workflow with approve/reject</li>
                <li>All changes logged in history</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6><History size={18} className="me-2 text-info" />Audit Trail</h6>
              <ul className="small mb-0">
                <li>Complete change history</li>
                <li>Track who made what changes</li>
                <li>Timestamps for all modifications</li>
                <li>Revert capability for admins</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Try It Section */}
      <div className="alert alert-info mt-4">
        <h5><AlertCircle size={20} className="me-2" />Try These Features:</h5>
        <ol className="mb-0">
          <li>Switch between Admin and Teacher roles to see permission differences</li>
          <li><strong>Master Timetable:</strong> Filter by a teacher, then click + to add a lesson</li>
          <li><strong>As Admin:</strong> Drag a lesson card to move it to a different day/period</li>
          <li><strong>As Teacher:</strong> Try to delete a lesson - it creates a change request instead</li>
          <li><strong>Cover Availability:</strong> Click periods to toggle your availability</li>
          <li><strong>Cover Requests:</strong> Request cover and see available teachers</li>
          <li>Check Change Requests tab to approve/reject pending requests</li>
          <li>View History to see all logged changes</li>
        </ol>
      </div>
    </div>
  );
};

export default IshworldTimetableCoverDemo;